import {action, makeObservable, observable} from 'mobx';
import I18n from 'app/i18n';
import {showBetForm} from 'app/components/table';
import MyBetInterface from 'app/interfaces/MyBetInterface';
import MySportsbookBetInterface from 'app/interfaces/MySportsbookBetInterface';
import Get from 'app/utils/Get';
import UserCasinoLogInterface from 'app/interfaces/casino/CasinoLogInterface';
import {betMoney, getLinkPath} from 'app/utils';
import {toastError, toastSuccess} from 'app/components/toasts/liteToast';
import {buyoutStore} from 'app/store/BuyoutStore';
import {BuyoutEventPayloadInterface} from 'app/interfaces/BuyoutEventPayloadInterface';

const BUYOUT_RETRYABLE_REASONS = ['moderator', 'coef_changed'];

interface UserBetsInterface {
  bets: Array<MyBetInterface | MySportsbookBetInterface>;
  has_more: boolean;
}

interface FilterInterface {
    has_more: boolean,
    page: number,
    type: string,
    bet_type?: string
    bet_currency?: string
    product_type?: string
}

interface CasinoFilterInterface {
    page: number,
    action: string,
    has_more: boolean
}

interface UserCasinoLogsInterface {
    data: UserCasinoLogInterface[],
    has_more: boolean
}

export class UserBetsStore {
    @observable casinoLogs = [];

    @observable userBets = [];

    @observable casinoFilter: CasinoFilterInterface = {
        action: 'all',
        has_more: true,
        page: 1
    };

    @observable filter: FilterInterface = {
        bet_currency: 'all',
        bet_type: 'all',
        has_more: true,
        page: 1,
        product_type: 'all',
        type: 'all'
    };

    @observable loading = false;

    @observable openedUserBetId = 0;

    constructor() {
        makeObservable(this);
    }

    @action
        setLoading = (value: boolean): void => {
            this.loading = value;
        };

    @action
        setOpenedUserBetId = (value: number): void => {
            this.openedUserBetId = value;
        };

    @action
        setDefaultAllFilters = (): void => {
            this.filter.type = 'all';
            this.filter.product_type = 'all';
            this.setDefaultBetsFilters();
            this.setDefaultCasinoFilters();
        };

    @action
        setDefaultBetsFilters = (type = 'all'): void => {
            this.filter.bet_currency = type === 'sport' ? 'money' : 'all';
            this.filter.bet_type = 'all';
            this.filter.page = 1;
        };

    @action
        setDefaultCasinoFilters = (): void => {
            this.casinoFilter.action = 'all';
            this.casinoFilter.page = 1;
        };

    @action
        setDefaultParams = (): void => {
            this.userBets = [];
            this.filter.page = 1;
        };

    @action
        changeCasinoAction = (value: string): void => {
            this.casinoFilter.action = value;
        };

    @action
        changeBetType = (value: string): void => {
            this.filter.bet_type = value;
            this.loadBets(this.filter.type);
        };

    @action
        changeBetCurrency = (value: string): void => {
            this.filter.bet_currency = value;
            this.loadBets(this.filter.type);
        };

    @action
        update = (data: UserBetsInterface, reset = true): void => {
            if (data && data.bets) {
                this.userBets = reset ? data.bets : this.userBets.concat(data.bets);
                this.filter.page += 1;
                buyoutStore.watchBets(this.userBets);
            }
            this.filter.has_more = data && data.has_more;
        };

    @action
        changeType = (value: string, resetFilters = false): void => {
            if (resetFilters) {
                this.filter.bet_currency = this.filter.product_type === 'sport' ? 'money' : 'all';
                this.filter.bet_type = 'all';
            }
            this.filter.type = value;
        };

    @action
        changeProductType = (value: string): void => {
            if (value === 'casino') {
                this.setDefaultCasinoFilters();
                this.loadLogs('all');
            } else {
                this.setDefaultParams();
            }
            this.filter.type = 'all';
            this.setDefaultBetsFilters(value);
            this.filter.product_type = value;
        };

    loadBetsHistory = (successLoadFunction: (response: UserBetsInterface) => void): Promise<void> => {
        const params = this.getFilterParams();
        this.setLoading(true);
        return new Get({
            params: {...params}, url: getLinkPath('/my_bets')
        })
            .execute()
            .then(response => response.json())
            .then(data => {
                successLoadFunction(data);
            }).finally(() => {
                this.setLoading(false);
            });
    };

    loadBetsHandler = (): Promise<void> => this.loadBetsHistory(response => this.update(response));

    updateLoadedBetsHandler = (): Promise<void> => this.loadBetsHistory(response => this.update(response, false));

    @action
        filterParams = (page: number): void => {
            this.filter.page = page || this.filter.page;
            this.filter.has_more = false;
        };

    getFilterParams = (): FilterInterface => ({
        bet_currency: this.filter.bet_currency,
        bet_type: this.filter.bet_type,
        has_more: this.filter.has_more,
        page: this.filter.page,
        product_type: this.filter.product_type,
        type: this.filter.type
    });

    loadBets = (type: string, isProductType = false): void => {
        if (isProductType) {
            this.changeProductType(type);
        } else {
            this.changeType(type);
        }
        this.filterParams(1);
        this.loadBetsHandler();
    };

    loadLogs = (logAction: string): void => {
        this.changeCasinoAction(logAction);
        this.updateCasinoPage(1);
        this.loadCasinoLogs();
    };

    @action
        updateCancelledUserBet = (id: string): void => {
            if (this.filter.bet_type === 'active') {
                this.userBets.splice(this.userBets.findIndex(el => el.id === id), 1);
            } else {
                const bet = this.userBets.find(u => id === u.id);
                bet.started = false;
                bet.cancelled = true;
                bet.cancelled_reason = I18n.t('user_cancel_bet.reason');
            }
        };

    handleBuyoutEvent = (event: string, payload: BuyoutEventPayloadInterface): void => {
        buyoutStore.clearPending(String(payload.user_bet_id));

        if (event === 'buyout_accepted') {
            this.applyBuyoutAccepted(payload);
            toastSuccess(I18n.t('buyout.accepted', {
                amount: betMoney({amount: payload.amount, playmoney: payload.playmoney})
            }));
        } else {
            this.applyBuyoutRejected(payload);
            toastError(I18n.t('buyout.rejected'));
        }
    };

    @action
        applyBuyoutAccepted = (payload: BuyoutEventPayloadInterface): void => {
            const id = String(payload.user_bet_id);
            const index = this.userBets.findIndex(el => String(el.id) === id);

            if (index < 0) {
                return;
            }

            if (this.filter.bet_type === 'active') {
                this.userBets.splice(index, 1);
                return;
            }

            const bet = this.userBets[index];
            bet.bought_out = true;
            bet.buyout_available = false;
            bet.buyout = null;
            bet.finished = true;
            bet.win = payload.amount;
        };

    @action
        applyBuyoutRejected = (payload: BuyoutEventPayloadInterface): void => {
            const bet = this.userBets.find(el => String(el.id) === String(payload.user_bet_id));

            if (bet) {
                bet.buyout = null;
                bet.buyout_available ||= BUYOUT_RETRYABLE_REASONS.includes(payload.reason);
            }
        };

    betMore = (bet: { hash_id: string, id: number }): void => {
        showBetForm(bet, null, {afterBet: this.loadBetsHandler});
    };

    @action
        updateCasinoPage = (value: number): void => {
            this.casinoFilter.page = value;
        };

    @action
        updateCasinoLogs = (data: UserCasinoLogsInterface): void => {
            if (this.casinoFilter.page > 1) {
                this.casinoFilter.has_more = data.has_more;
                this.casinoLogs = this.casinoLogs.concat(data.data);
            } else {
                this.casinoLogs = data.data;
                this.casinoFilter.has_more = data.has_more;
            }
        };

    @action
        loadCasinoLogs = (next_page = false): Promise<void> => {
            if (next_page) {
                this.increasePage();
            }

            this.setLoading(true);
            return new Get({
                params: {
                    casino_action: this.casinoFilter.action,
                    page: this.casinoFilter.page,
                    type: 'casino'
                },
                url: getLinkPath('/my_bets')
            })
                .execute()
                .then(response => response.json()).then(data => {
                    this.updateCasinoLogs(data);
                }).finally(() => {
                    this.setLoading(false);
                });
        };

    increasePage = (): void => {
        this.updateCasinoPage(this.casinoFilter.page + 1);
    };
}
